import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import RoleCard from './components/RoleCard';
import ProgressIndicator from './components/ProgressIndicator';
import profileService from '../../services/profileService';

const ProfileCompletionWizardStep1 = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Redirect if profile already completed
    if (userProfile?.profile_completed) {
      const roleRoutes = {
        superadmin: '/super-admin-dashboard',
        client: '/client-job-management',
        contractor: '/contractor-dashboard',
        affiliate: '/affiliate-dashboard'
      };
      navigate(roleRoutes?.[userProfile?.role] || '/contractor-dashboard');
    }
  }, [userProfile, navigate]);

  const getRoleConfig = () => {
    const configs = {
      contractor: {
        title: 'Vakman',
        iconName: 'Wrench',
        description: 'Als vakman krijgt u toegang tot opdrachten van opdrachtgevers en kunt u uw vaardigheden en beschikbaarheid beheren.',
        benefits: [
          'Toegang tot relevante opdrachten in uw vakgebied',
          'Beheer uw vaardigheden en certificeringen',
          'Stel uw beschikbaarheid en tarieven in',
          'Bouw uw professionele profiel op'
        ],
        nextSteps: [
          'Vaardigheden en expertise selecteren',
          'Certificeringen en ervaring toevoegen',
          'Beschikbaarheid en tarieven instellen'
        ]
      },
      client: {
        title: 'Opdrachtgever',
        iconName: 'Building2',
        description: 'Als opdrachtgever kunt u opdrachten plaatsen en gekwalificeerde vakmensen vinden voor uw projecten.',
        benefits: [
          'Plaats opdrachten en ontvang sollicitaties',
          'Toegang tot gekwalificeerde vakmensen',
          'Beheer uw bedrijfsgegevens',
          'Volg de voortgang van uw projecten'
        ],
        nextSteps: [
          'Bedrijfsgegevens invullen',
          'Facturatiegegevens toevoegen',
          'Voorkeuren voor diensten instellen'
        ]
      },
      affiliate: {
        title: 'Partner',
        iconName: 'Users',
        description: 'Als partner kunt u verwijzingen doen en commissies verdienen door vakmensen en opdrachtgevers te verbinden.',
        benefits: [
          'Verdien commissies op verwijzingen',
          'Toegang tot zowel opdrachten als vakmensen',
          'Beheer uw netwerk en verwijzingen',
          'Volg uw commissie-inkomsten'
        ],
        nextSteps: [
          'Bedrijfs- en netwerkgegevens invullen',
          'Verwijzingsmogelijkheden instellen',
          'Commissievoorkeuren configureren'
        ]
      },
      superadmin: {
        title: 'Super Admin',
        iconName: 'Shield',
        description: 'Als super admin heeft u volledige toegang tot het platform en alle beheerfuncties.',
        benefits: [
          'Volledige toegang tot alle functies',
          'Gebruikers- en platformbeheer',
          'Inzicht in alle activiteiten',
          'Configuratie van platforminstellingen'
        ],
        nextSteps: []
      }
    };

    return configs?.[userProfile?.role] || configs?.contractor;
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      // Update profile completion step
      await profileService?.updateProfileCompletion(user?.id, 1);
      
      // Navigate to step 2
      navigate('/profile-completion-wizard-step-2-role-specific-information');
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeRole = () => {
    // In a real implementation, this would allow role change
    // For now, just show a message
    alert('Neem contact op met support om uw rol te wijzigen.');
  };

  const roleConfig = getRoleConfig();

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            Welkom bij het platform!
          </h1>
          <p className="text-lg text-muted-foreground">
            Laten we uw profiel compleet maken om te beginnen
          </p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator
          currentStep={1}
          totalSteps={3}
          estimatedTime="5-10 minuten"
        />

        {/* Role Confirmation */}
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">
              Uw geselecteerde rol
            </h2>
            <p className="text-muted-foreground">
              Controleer of dit de juiste rol voor u is
            </p>
          </div>

          <RoleCard
            role={userProfile?.role}
            title={roleConfig?.title}
            description={roleConfig?.description}
            benefits={roleConfig?.benefits}
            iconName={roleConfig?.iconName}
          />

          {/* Next Steps Preview */}
          {roleConfig?.nextSteps && roleConfig?.nextSteps?.length > 0 && (
            <div 
              className="bg-muted/50 border-2 border-slate-200 rounded-lg p-6 space-y-4"
              style={{
                boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
              }}
            >
              <h3 className="text-lg font-semibold text-foreground">
                Volgende stappen
              </h3>
              <p className="text-sm text-muted-foreground">
                In de volgende stap verzamelen we de volgende informatie:
              </p>
              <ul className="space-y-2">
                {roleConfig?.nextSteps?.map((step, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="mt-0.5">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Trust Signals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div 
              className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg"
              style={{
                boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
              }}
            >
              <p className="text-2xl font-bold text-primary">1000+</p>
              <p className="text-sm text-muted-foreground">Actieve gebruikers</p>
            </div>
            <div 
              className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg"
              style={{
                boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
              }}
            >
              <p className="text-2xl font-bold text-primary">500+</p>
              <p className="text-sm text-muted-foreground">Voltooide opdrachten</p>
            </div>
            <div 
              className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg"
              style={{
                boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
              }}
            >
              <p className="text-2xl font-bold text-primary">4.8/5</p>
              <p className="text-sm text-muted-foreground">Gemiddelde beoordeling</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={handleChangeRole}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Rol Wijzigen
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={handleContinue}
            loading={isLoading}
            iconName="ArrowRight"
            iconPosition="right"
          >
            Doorgaan
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionWizardStep1;